import io from "socket.io-client";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY, patchAppUser } from "./appAuthSlice";
import {
  clearBondWheelError,
  fetchBondWheelConstants,
  fetchBondWheelGames,
  setBondWheelActionLoading,
  setBondWheelLastSettledGame,
  upsertBondWheelGame,
} from "./bondWheelSlice";
import {
  getEntryUserId,
  getUserWinnerEntry,
  isUserWinner,
} from "../utils/bondWheelHelpers";

let socket = null;
let initialized = false;

export const getBondWheelSocket = () => socket;

const emitWithToken = (event, payload, ack) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  socket?.emit(event, { ...(payload || {}), token }, ack);
};

const emitWithAck = (event, payload, timeoutMs = 45000) =>
  new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error(`Not connected to server (${API_BASE_URL})`));
      return;
    }
    const timer = setTimeout(() => reject(new Error("Request timed out. Please try again.")), timeoutMs);
    emitWithToken(event, payload, (res) => {
      clearTimeout(timer);
      if (!res) {
        reject(new Error("No response from server"));
        return;
      }
      if (res.error) {
        reject(new Error(res.error));
        return;
      }
      if (res.ok === false) {
        reject(new Error(res.error || "Bond Wheel request failed"));
        return;
      }
      resolve(res);
    });
  });

const adjustCoinBalance = (dispatch, getState, delta) => {
  const current = Number(getState?.()?.appAuth?.user?.COIN || 0);
  if (!Number.isFinite(current)) return;
  dispatch(patchAppUser({ COIN: Math.max(0, current + delta) }));
};

export const initBondWheelSocket = (dispatch, getState) => {
  if (initialized && socket?.connected) return socket;
  initialized = true;

  if (!socket) {
    socket = io(API_BASE_URL, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 30000,
      upgrade: false,
      forceNew: false,
      withCredentials: false,
    });
  }

  socket.off("connect");
  socket.off("bondWheel:game:created");
  socket.off("bondWheel:game:updated");
  socket.off("bondWheel:game:locked");
  socket.off("bondWheel:game:settled");
  socket.off("bondWheel:game:cancelled");

  socket.on("connect", () => {
    dispatch(fetchBondWheelGames({ limit: 60 }));
  });

  socket.on("bondWheel:game:created", ({ game }) => {
    if (game) dispatch(upsertBondWheelGame(game));
  });

  socket.on("bondWheel:game:updated", ({ game }) => {
    if (game) dispatch(upsertBondWheelGame(game));
  });

  socket.on("bondWheel:game:locked", ({ gameId }) => {
    if (!gameId) return;
    const games = getState?.()?.bondWheel?.games || [];
    const existing = games.find((g) => String(g._id) === String(gameId));
    if (existing) {
      dispatch(upsertBondWheelGame({ ...existing, status: "Locked" }));
    } else {
      dispatch(fetchBondWheelGames({ limit: 60 }));
    }
  });

  socket.on("bondWheel:game:settled", ({ game }) => {
    if (!game) return;
    dispatch(upsertBondWheelGame(game));
    dispatch(setBondWheelLastSettledGame(game));
    dispatch(fetchBondWheelGames({ status: "Settled", limit: 100 }));

    const userId = getState?.()?.appAuth?.user?._id;
    if (!userId) return;
    const uid = String(userId);
    const creatorId = String(game.creator?._id || game.creator || "");

    if (isUserWinner(game, uid)) {
      const myEntry = getUserWinnerEntry(game, uid);
      adjustCoinBalance(dispatch, getState, Number(myEntry?.netPayout ?? game.winnerNetPayout ?? 0));
    }
    if (creatorId === uid) {
      const creatorFee = Number(game.totalCreatorFee ?? game.creatorFee ?? 0);
      if (creatorFee > 0) {
        adjustCoinBalance(dispatch, getState, creatorFee);
      }
    }
  });

  socket.on("bondWheel:game:cancelled", ({ game }) => {
    if (!game) return;
    dispatch(upsertBondWheelGame(game));
    dispatch(fetchBondWheelGames({ limit: 60 }));
    const userId = getState?.()?.appAuth?.user?._id;
    if (!userId) return;
    const uid = String(userId);
    const myEntry = (game.entries || []).find((entry) => getEntryUserId(entry) === uid);
    if (myEntry?.status === "Refunded") {
      adjustCoinBalance(dispatch, getState, Number(myEntry.entryAmount || game.entryAmount || 0));
    }
  });

  if (!socket.connected) socket.connect();
  dispatch(fetchBondWheelGames({ limit: 60 }));
  dispatch(fetchBondWheelConstants());
  return socket;
};

export const emitBondWheelCreate = (payload) => emitWithAck("bondWheel:create", payload);
export const emitBondWheelJoin = (payload) => emitWithAck("bondWheel:join", payload);
export const emitBondWheelLeave = (payload) => emitWithAck("bondWheel:leave", payload);

export const runBondWheelCreate = async (payload, { dispatch, getState, rejectWithValue } = {}) => {
  try {
    const res = await emitBondWheelCreate(payload);
    if (res?.game && dispatch) {
      dispatch(upsertBondWheelGame(res.game));
      if (!res.reused) {
        adjustCoinBalance(dispatch, getState, -Number(res.game.entryAmount || payload.entryAmount || 0));
      }
    }
    return res;
  } catch (e) {
    const message = e?.message || "Could not create Bond Wheel.";
    if (rejectWithValue) return rejectWithValue({ error: message, message });
    throw e;
  }
};

export const runBondWheelJoin = (payload) => async (dispatch, getState) => {
  dispatch(setBondWheelActionLoading(true));
  dispatch(clearBondWheelError());
  try {
    const res = await emitBondWheelJoin(payload);
    if (res?.game) {
      dispatch(upsertBondWheelGame(res.game));
      if (!res.reused) {
        const stakeAmount =
          Number(res.stakeAmount) ||
          Number(res.game.entryAmount || 0) * Math.max(1, Math.floor(Number(payload?.ticketCount) || 1));
        adjustCoinBalance(dispatch, getState, -stakeAmount);
      }
    }
    return res;
  } finally {
    dispatch(setBondWheelActionLoading(false));
  }
};

export const runBondWheelLeave = (payload) => async (dispatch, getState) => {
  dispatch(setBondWheelActionLoading(true));
  dispatch(clearBondWheelError());
  try {
    const res = await emitBondWheelLeave(payload);
    if (res?.game) {
      dispatch(upsertBondWheelGame(res.game));
      adjustCoinBalance(dispatch, getState, Number(res.refundAmount || 0));
    }
    return res;
  } finally {
    dispatch(setBondWheelActionLoading(false));
  }
};
